import {FC, FormEvent, useEffect, useState} from 'react'
import {Activity, Building2, Check, Database, Save, Tags, UsersRound, X} from 'lucide-react'

import * as API from '../../api'

interface Props {
  companyData?: any
  onCompanyCreated: () => void
  onCompanyUpdated: () => void
  onClose: () => void
}

const parseExternalIds = (companyData?: any) => {
  try {
    if (!companyData?.id_external_bi) return ''
    const parsed = JSON.parse(companyData.id_external_bi)
    return Array.isArray(parsed.id_external) ? parsed.id_external.join(', ') : ''
  } catch {
    return ''
  }
}

const CompanyModal: FC<Props> = ({companyData, onCompanyCreated, onCompanyUpdated, onClose}) => {
  const [loading, setLoading] = useState(false)
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [name, setName] = useState('')
  const [maxUsers, setMaxUsers] = useState<number | ''>(10)
  const [externalIds, setExternalIds] = useState('')
  const [companyType, setCompanyType] = useState<string>('')
  const [companyTypes, setCompanyTypes] = useState<any[]>([])
  const [isActived, setIsActived] = useState(1)
  const [error, setError] = useState('')

  useEffect(() => {
    setName(companyData?.name || '')
    setMaxUsers(Number(companyData?.max_users) || 10)
    setExternalIds(parseExternalIds(companyData))
    setCompanyType(companyData?.id_type_company ? `${companyData.id_type_company}` : '')
    setIsActived(companyData ? Number(companyData.is_actived) : 1)
    setError('')
  }, [companyData])

  useEffect(() => {
    let mounted = true

    async function loadCompanyTypes() {
      setLoadingTypes(true)
      try {
        const response = await API.getCompanyTypes()
        const types = Array.isArray(response.data) ? response.data : []
        if (!mounted) return
        setCompanyTypes(types)
        setCompanyType((current) => current || `${types[0]?.id_type_company || ''}`)
      } catch {
        if (mounted) setError('Não foi possível carregar os tipos de empresa.')
      } finally {
        if (mounted) setLoadingTypes(false)
      }
    }

    loadCompanyTypes()
    return () => {
      mounted = false
    }
  }, [])

  const validationMessage =
    name.trim().length < 2
      ? 'Informe um nome com pelo menos 2 caracteres.'
      : Number(maxUsers) < 1
      ? 'O limite de usuários deve ser igual ou maior que 1.'
      : !companyType
      ? 'Selecione o tipo da empresa.'
      : ''

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (validationMessage || loading) return
    setLoading(true)
    setError('')

    const normalizedExternalIds = externalIds
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
    const payload = {
      name: name.trim(),
      is_actived: isActived,
      id_type_company: companyType,
      id_external_bi: JSON.stringify({id_external: normalizedExternalIds}),
      max_users: Number(maxUsers),
      company_guid: companyData?.guid,
    }

    try {
      const response = companyData
        ? await API.updateCompany(payload)
        : await API.createCompany(payload)
      if (response?.success === false) throw new Error('Company save failed')
      if (companyData) onCompanyUpdated()
      else onCompanyCreated()
    } catch {
      setError('Não foi possível salvar a empresa. Revise os dados e tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className='ca-company-modal' onSubmit={handleSubmit}>
      <header className='ca-company-modal__header'>
        <span className='ca-company-modal__header-icon'>
          <Building2 size={22} />
        </span>
        <div>
          <small>{companyData ? 'Atualização de cadastro' : 'Nova organização'}</small>
          <h2>{companyData ? 'Editar empresa' : 'Adicionar empresa'}</h2>
          <p>Defina os dados básicos, a capacidade e a disponibilidade deste cadastro.</p>
        </div>
        <button
          type='button'
          className='ua-user-modal__close'
          aria-label='Fechar'
          disabled={loading}
          onClick={onClose}
        >
          <X size={17} />
        </button>
      </header>

      <div className='ca-company-modal__body'>
        <section className='ca-company-modal__card'>
          <div className='ca-company-modal__intro'>
            <span>
              <Building2 size={17} />
            </span>
            <div>
              <h3>Dados da empresa</h3>
              <p>Informações utilizadas na identificação e nos limites de acesso.</p>
            </div>
          </div>

          <div className='ca-company-modal__grid'>
            <div className='ca-company-modal__field ca-company-modal__field--full'>
              <label htmlFor='company-name'>
                <Building2 size={13} /> Nome da empresa
              </label>
              <input
                id='company-name'
                value={name}
                autoFocus
                placeholder='Ex.: Compra Agora Distribuição'
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className='ca-company-modal__field'>
              <label htmlFor='company-type'>
                <Tags size={13} /> Tipo da empresa
              </label>
              <select
                id='company-type'
                value={companyType}
                disabled={loadingTypes}
                onChange={(event) => setCompanyType(event.target.value)}
              >
                {loadingTypes && <option value=''>Carregando tipos…</option>}
                {!loadingTypes && !companyTypes.length && (
                  <option value=''>Nenhum tipo disponível</option>
                )}
                {companyTypes.map((type) => (
                  <option key={type.id_type_company} value={type.id_type_company}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='ca-company-modal__field'>
              <label htmlFor='company-max-users'>
                <UsersRound size={13} /> Limite de usuários
              </label>
              <input
                id='company-max-users'
                type='number'
                min={1}
                value={maxUsers}
                placeholder='Número máximo de usuários'
                onChange={(event) =>
                  setMaxUsers(event.target.value === '' ? '' : Number(event.target.value))
                }
              />
            </div>

            <div className='ca-company-modal__field ca-company-modal__field--full'>
              <label htmlFor='company-external-ids'>
                <Database size={13} /> IDs externos
              </label>
              <input
                id='company-external-ids'
                value={externalIds}
                placeholder='Ex.: 1042, 3187, 9011'
                onChange={(event) => setExternalIds(event.target.value)}
              />
              <span className='ca-company-modal__hint'>
                Campo opcional. Separe múltiplos identificadores por vírgula.
              </span>
            </div>
          </div>
        </section>

        <section className='ca-company-modal__card'>
          <div className='ca-company-modal__status'>
            <div className='ca-company-modal__status-copy'>
              <span>
                <Activity size={17} />
              </span>
              <div>
                <strong>Status do cadastro</strong>
                <small>Controla se a empresa permanece disponível para uso na plataforma.</small>
              </div>
            </div>
            <div className='ua-segmented-control'>
              <label className={isActived === 1 ? 'is-active' : ''}>
                <input
                  type='radio'
                  name='company-status'
                  checked={isActived === 1}
                  onChange={() => setIsActived(1)}
                />
                {isActived === 1 && <Check size={12} />} Ativa
              </label>
              <label className={isActived === 0 ? 'is-active is-negative' : ''}>
                <input
                  type='radio'
                  name='company-status'
                  checked={isActived === 0}
                  onChange={() => setIsActived(0)}
                />
                Inativa
              </label>
            </div>
          </div>
        </section>

        {error && <div className='ua-feedback ua-feedback--danger'>{error}</div>}
      </div>

      <footer className='ca-company-modal__footer'>
        <span>
          {validationMessage || 'Os campos nome, tipo e limite de usuários são obrigatórios.'}
        </span>
        <div>
          <button
            type='button'
            className='ua-secondary-button'
            disabled={loading}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type='submit'
            className='ua-primary-button'
            disabled={Boolean(validationMessage || loading || loadingTypes)}
          >
            {loading ? <span className='ua-spinner' /> : <Save size={14} />}
            {companyData ? 'Salvar alterações' : 'Criar empresa'}
          </button>
        </div>
      </footer>
    </form>
  )
}

export default CompanyModal
